/*
 * KSTYLESHOP slideshow (sections/kstyle-slideshow.liquid).
 * Slides sit on top of each other and fade; only the active one is visible and focusable.
 * Previous/next arrows at the foot of the text column (no dots, no pause button), swipe on
 * touch screens and arrow keys. Optional autoplay pauses on hover/focus, stops for good once
 * the shopper changes slides themselves, and never runs for people who prefer reduced motion.
 * Slides hidden at the current screen size ("phones only" / "desktop only") are skipped.
 */
(() => {
  if (customElements.get('kstyle-slideshow')) return;

  class KstyleSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-slide-dot]')];
      this.pauseButton = this.querySelector('[data-slide-pause]');
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.interval = Math.max(3, Number(this.dataset.interval) || 6) * 1000;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.playing = this.dataset.autoplay === 'true' && !reduced && this.slides.length > 1;
      this.held = false;
      if (this.slides.length < 2) return;

      // A slide change the shopper asked for also stops autoplay.
      const step = (delta) => {
        this.setPlaying(false);
        this.move(delta);
      };

      this.querySelector('[data-slide-prev]')?.addEventListener('click', () => step(-1));
      this.querySelector('[data-slide-next]')?.addEventListener('click', () => step(1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => {
        this.setPlaying(false);
        this.go(i);
      }));
      this.pauseButton?.addEventListener('click', () => this.setPlaying(!this.playing));

      // Hold still while the pointer or keyboard focus is on the slideshow.
      this.addEventListener('mouseenter', () => this.hold(true));
      this.addEventListener('mouseleave', () => this.hold(false));
      this.addEventListener('focusin', () => this.hold(true));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.hold(false);
      });

      // Swipe: a horizontal drag of 40px or more changes the slide.
      const track = this.querySelector('[data-slide-track]');
      let startX = null;
      let startY = null;
      track.addEventListener('pointerdown', (event) => {
        startX = event.clientX;
        startY = event.clientY;
      });
      track.addEventListener('pointerup', (event) => {
        if (startX === null) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        startX = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) step(dx < 0 ? 1 : -1);
      });
      track.addEventListener('pointercancel', () => {
        startX = null;
      });

      // Keyboard: left and right arrow keys while focus is inside the slideshow.
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        step(event.key === 'ArrowRight' ? 1 : -1);
      });

      // Theme editor: show the slide whose block is selected.
      this.onBlockSelect = (event) => {
        const i = this.slides.findIndex((slide) => slide === event.target || slide.contains(event.target));
        if (i < 0) return;
        this.setPlaying(false);
        this.go(i);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.onVisibility = () => this.schedule();
      document.addEventListener('visibilitychange', this.onVisibility);

      // A slide limited to the other screen size must not stay active after a resize.
      this.phoneQuery = window.matchMedia('(max-width: 749px)');
      this.onResize = () => {
        if (!this.shown(this.slides[this.index])) this.move(1);
      };
      this.phoneQuery.addEventListener('change', this.onResize);
      this.onResize();

      this.setPlaying(this.playing);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.phoneQuery?.removeEventListener('change', this.onResize);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    shown(slide) {
      return getComputedStyle(slide).display !== 'none';
    }

    // Next (delta 1) or previous (-1) slide that is shown at this screen size.
    move(delta) {
      const count = this.slides.length;
      let i = this.index;
      for (let n = 0; n < count; n += 1) {
        i = (i + delta + count) % count;
        if (this.shown(this.slides[i])) break;
      }
      this.go(i);
    }

    go(i) {
      const count = this.slides.length;
      this.index = (i + count) % count;
      this.slides.forEach((slide, n) => {
        const active = n === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
      this.dots.forEach((dot, n) => dot.setAttribute('aria-current', String(n === this.index)));
      this.schedule();
    }

    hold(on) {
      this.held = on;
      this.schedule();
    }

    setPlaying(on) {
      this.playing = on;
      if (this.pauseButton) {
        this.pauseButton.setAttribute('aria-pressed', String(!on));
        this.pauseButton.setAttribute('aria-label', on ? 'Pause slideshow' : 'Play slideshow');
        this.pauseButton.classList.toggle('is-paused', !on);
      }
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.playing || this.held || document.hidden) return;
      this.timer = setTimeout(() => this.move(1), this.interval);
    }
  }

  customElements.define('kstyle-slideshow', KstyleSlideshow);
})();
